import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/loading-animation.json';

interface LoadingStateProps {
  message?: string;
  type?: 'default' | 'verification' | 'booking' | 'registration';
}

const statusMessages = {
  default: [
    'Establishing secure connection...',
    'Syncing with server...',
    'Almost ready...'
  ],
  verification: [
    'Securing encrypted tunnel...',
    'Searching medical records...',
    'Authenticating identity...',
    'Finalizing verification...'
  ],
  booking: [
    'Checking calendar availability...',
    'Reserving your time slot...',
    'Securing appointment details...',
    'Confirming with practice...'
  ],
  registration: [
    'Creating secure profile...',
    'Encrypting personal data...',
    'Syncing insurance information...',
    'Finalizing account...'
  ]
};

export default function LoadingState({ message, type = 'default' }: LoadingStateProps) {
  const messages = statusMessages[type as keyof typeof statusMessages] || statusMessages.default;
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center py-16 px-4"
    >
      <div className="relative w-32 h-24 sm:w-48 sm:h-32 mb-8">
        {/* Glow effect */}
        <div className="absolute inset-0 bg-primary/20 blur-[40px] rounded-full animate-pulse" />
        
        <div className="relative z-10 w-full h-full flex items-center justify-center">
          <Lottie
            animationData={loadingAnimation}
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </div>

      <div className="text-center max-w-xs mx-auto">
        <motion.h3 
          className="text-xl font-bold text-gray-900 mb-2 tracking-tight"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {message || 'Processing Request'}
        </motion.h3>
        
        <div className="h-6 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessageIndex}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.5, ease: "circOut" }}
              className="text-sm text-gray-500 font-medium tracking-wide uppercase"
            >
              {messages[currentMessageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* Modern Progress Bar */}
      <div className="w-32 sm:w-48 h-1 bg-gray-100 rounded-full mt-8 overflow-hidden relative">
        <motion.div 
          className="absolute inset-y-0 left-0 bg-primary rounded-full"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ 
            duration: 10, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        />
      </div>

      <div className="mt-12 flex items-center gap-2 text-[10px] text-gray-500 font-bold tracking-[0.2em] uppercase">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        End-to-End Encrypted
      </div>
    </motion.div>
  );
}
